import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, CheckSquare, Square, type LucideIcon, ChevronDown } from 'lucide-react';

interface DropdownOption {
    label: string;
    value: string;
}

interface MultiSelectDropdownProps {
    label?: string;
    options: DropdownOption[];
    value?: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    icon?: LucideIcon | null;
    searchInputPlaceholder?: string;
    error?: string;
    required?: boolean;
    maxVisibleTags?: number;
    width?: string;
    variant?: 'default' | 'project' | 'config';
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    options = [],
    value = [],
    onChange,
    disabled = false,
    placeholder = "Select items",
    className = "",
    icon: Icon = null,
    searchInputPlaceholder = 'Type to search...',
    error,
    required,
    maxVisibleTags = 2,
    width = "w-full",
    variant = "default"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 280;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                (!menuRef.current || !menuRef.current.contains(target))
            ) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (showSearch && isOpen && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showSearch, isOpen]);

    const toggleDropdown = () => {
        if (!disabled) {
            calculateDropdownPosition();
            setIsOpen(!isOpen);
            if (isOpen) {
                setShowSearch(false);
                setSearchTerm('');
            }
        }
    };

    const handleItemToggle = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const isSelected = value.includes(optionValue);
        let newValues;
        if (isSelected) {
            newValues = value.filter(v => v !== optionValue);
        } else {
            newValues = [...value, optionValue];
        }
        onChange(newValues);
    };

    const handleRemoveTag = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newValues = value.filter(v => v !== optionValue);
        onChange(newValues);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const visibleTags = value.slice(0, maxVisibleTags);
    const hiddenTagCount = value.length - maxVisibleTags;

    const getLabel = (val: string) => {
        return options.find(opt => opt.value === val)?.label || val;
    };

    return (
        <div className={`space-y-1 ${className} ${width}`} ref={dropdownRef}>
            {label && (
                variant === 'project' ? (
                    <label className="block text-sm font-semibold text-gray-300 mb-1 flex items-center gap-2">
                        {Icon && <Icon className="w-4 h-4 text-orange-400" />}
                        {label} {required && <span className="text-red-400 text-base">*</span>}
                    </label>
                ) : variant === 'config' ? (
                    <label className="flex items-center gap-1.5 text-xs font-bold text-gray-200 mb-1">
                        {Icon && <Icon className="w-3.5 h-3.5 text-orange-500" />}
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                ) : (
                    <label className="flex items-center gap-1.5 text-[9px] font-black text-slate-300 uppercase tracking-widest leading-none mb-0.5">
                        {Icon && <Icon className="w-3 h-3 text-orange-400" />}
                        {label}
                        {required && <span className='text-red-400'>*</span>}
                    </label>
                )
            )}

            <div className="relative">
                <div
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    className={`relative w-full cursor-pointer rounded-lg border text-left shadow-md transition-all duration-300 flex items-center
                        ${variant === 'project'
                            ? `bg-gray-700/80 px-4 py-3 min-h-[48px] ${disabled ? 'border-gray-600' : 'hover:bg-gray-700 hover:border-gray-500'} ${isOpen ? 'border-orange-500 bg-gray-700 ring-2 ring-orange-500/20' : 'border-gray-600'}`
                            : variant === 'config'
                                ? `bg-slate-900 px-3 py-1.5 min-h-[48px] border-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed border-slate-800' : 'hover:border-gray-600 hover:bg-slate-900'} ${isOpen ? 'border-orange-500 bg-slate-900 ring-2 ring-orange-500/20' : ''}`
                                : `bg-slate-900 px-3 py-1 min-h-[32px] bg-slate-950/40 ${disabled ? 'opacity-50 cursor-not-allowed border-slate-800' : 'hover:bg-slate-800 hover:border-slate-600'} ${isOpen ? 'border-orange-500 bg-slate-800 ring-2 ring-orange-500/20' : 'border-slate-800/80'}`
                        }
                        focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 ${className}`}
                >
                    <div className="flex-1 flex flex-wrap items-center gap-1 min-w-0 pr-6">
                        {value.length > 0 ? (
                            <>
                                {visibleTags.map((val) => (
                                    <span
                                        key={val}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-[10px] font-bold text-slate-200"
                                    >
                                        <span className="truncate max-w-[80px]">{getLabel(val)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveTag(val, e)}
                                            className="hover:bg-slate-700 rounded-full p-0.5"
                                        >
                                            <X className="w-2.5 h-2.5 text-slate-400 hover:text-orange-400" />
                                        </button>
                                    </span>
                                ))}
                                {hiddenTagCount > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[9px] font-black text-orange-400 uppercase">
                                        +{hiddenTagCount}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className={`block truncate ${
                                variant === 'project'
                                    ? 'text-gray-400 text-sm'
                                    : variant === 'config'
                                        ? 'text-gray-400 text-[13px] font-medium'
                                        : 'text-slate-200 text-[11px] font-medium'
                            }`}>
                                {placeholder}
                            </span>
                        )}
                    </div>

                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
                        {value.length > 0 && (
                            <button
                                onClick={handleClearSelection}
                                className="p-1 hover:bg-slate-800 rounded-full transition-colors"
                                type="button"
                            >
                                <X className="w-3 h-3 text-slate-500 hover:text-orange-400" />
                            </button>
                        )}
                        <ChevronDown className={`h-3.5 w-3.5 text-slate-200 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                </div>

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] max-h-64 overflow-hidden rounded-xl bg-slate-900 shadow-2xl border border-slate-800 animate-in fade-in-0 zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle }}
                    >
                        <div className="flex flex-col h-full">
                            <div className="p-2 border-b border-slate-800 bg-slate-900">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 focus:outline-none transition-all placeholder:text-slate-700 font-medium"
                                        placeholder={searchInputPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>

                            <div className="overflow-y-auto custom-scroll flex-1 max-h-[200px]">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((opt) => {
                                        const isSelected = value.includes(opt.value);
                                        return (
                                            <div
                                                key={opt.value}
                                                onClick={(e) => handleItemToggle(opt.value, e)}
                                                className={`w-full cursor-pointer py-2 px-3 flex items-start gap-2.5 text-[11px] font-semibold transition-all duration-150 hover:bg-slate-800 ${isSelected ? "bg-orange-500/5" : "text-slate-300"}`}
                                            >
                                                <div className="flex-shrink-0 pt-0.5">
                                                    {isSelected ? (
                                                        <CheckSquare className="w-3.5 h-3.5 text-orange-400" />
                                                    ) : (
                                                        <Square className="w-3.5 h-3.5 text-slate-600" />
                                                    )}
                                                </div>
                                                <span className={`block truncate ${isSelected ? "text-white font-bold" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
                                            No results found
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
            {error && <span className='text-red-400 text-[10px] font-bold mt-1 uppercase tracking-wider'>{error}</span>}
        </div>
    );
};

export default MultiSelectDropdown;
